/**
 * Google Translate auto-translation layer.
 *
 * Purpose: for any page in the app that hasn't been manually translated via
 * react-i18next JSON files, this uses Google Translate to auto-translate the
 * whole DOM into the user's chosen language. It's a fallback safety net —
 * react-i18next translations still take priority (Google leaves already-swapped
 * text alone because the JSON has already changed the source text).
 *
 * Uses the `googtrans` cookie pattern which Google Translate reads on load
 * to trigger translation immediately, no visible widget UI.
 */
import Cookies from 'js-cookie';

const SCRIPT_URL = 'https://translate.google.com/translate_a/element.js?cb=__jobastiGoogleTranslateInit';
const COOKIE_NAME = 'googtrans';

let scriptLoaded = false;
let scriptLoading = false;

const setGoogTransCookie = (target) => {
  // Google reads `/en/de` format (source/target). We always translate from EN
  // because our source strings are English. Set on multiple domain scopes so
  // it survives subdomain differences.
  const value = target === 'en' ? '' : `/en/${target}`;
  const hostParts = window.location.hostname.split('.');
  const rootDomain = hostParts.length >= 2 ? '.' + hostParts.slice(-2).join('.') : window.location.hostname;
  try {
    if (value) {
      Cookies.set(COOKIE_NAME, value, { expires: 365, sameSite: 'lax' });
      Cookies.set(COOKIE_NAME, value, { expires: 365, sameSite: 'lax', domain: rootDomain });
    } else {
      Cookies.remove(COOKIE_NAME);
      Cookies.remove(COOKIE_NAME, { domain: rootDomain });
    }
  } catch { /* noop */ }
};

const loadScript = () => {
  if (scriptLoaded || scriptLoading) return;
  scriptLoading = true;
  // Global callback that Google's script invokes after loading.
  window.__jobastiGoogleTranslateInit = () => {
    try {
      /* global google */
      if (!window.google || !window.google.translate) return;
      new window.google.translate.TranslateElement(
        {
          pageLanguage: 'en',
          includedLanguages: 'en,de,fr,es,it,nl,pl,pt',
          autoDisplay: false,
          layout: window.google.translate.TranslateElement.InlineLayout.SIMPLE,
        },
        'google_translate_element'
      );
      scriptLoaded = true;
      scriptLoading = false;
    } catch { scriptLoading = false; }
  };
  const s = document.createElement('script');
  s.src = SCRIPT_URL;
  s.async = true;
  s.defer = true;
  document.head.appendChild(s);
};

/**
 * Trigger auto-translation of the whole page to `target` (ISO 639-1 like 'de').
 * If target is 'en', we undo any prior Google translation (back to source).
 * Tries the smooth path first (programmatically fires the hidden Google widget's
 * <select>) and falls back to a page reload if the widget isn't ready yet.
 */
export const setGoogleTranslateLanguage = (target) => {
  if (!target) return;
  const normalized = String(target).toLowerCase().split('-')[0];
  const currentGoog = Cookies.get(COOKIE_NAME) || '';
  const nextGoog = normalized === 'en' ? '' : `/en/${normalized}`;
  if (currentGoog === nextGoog) return;
  setGoogTransCookie(normalized);

  // Smooth path: fire the widget's <select> directly if it's already rendered.
  const tryTriggerWidget = () => {
    const select = document.querySelector('#google_translate_element select.goog-te-combo')
      || document.querySelector('select.goog-te-combo');
    if (!select) return false;
    select.value = normalized === 'en' ? '' : normalized;
    select.dispatchEvent(new Event('change'));
    return true;
  };

  if (tryTriggerWidget()) return;

  // Widget not ready yet — poll briefly (script may still be loading), then reload if needed.
  let attempts = 0;
  const timer = setInterval(() => {
    attempts += 1;
    if (tryTriggerWidget() || attempts > 12) {
      clearInterval(timer);
      if (attempts > 12 && document.readyState === 'complete') {
        // Last resort — force a reload so Google picks up the cookie on next load.
        window.location.reload();
      }
    }
  }, 250);
};

/** Lazy-init on demand — called once at app start. */
export const initGoogleTranslate = () => {
  loadScript();
};

/** Read the current googtrans cookie target, or 'en' if none. */
export const getGoogleTranslateLanguage = () => {
  const raw = Cookies.get(COOKIE_NAME);
  if (!raw) return 'en';
  const parts = raw.split('/');
  return parts[parts.length - 1] || 'en';
};
